import { useEffect, useRef, useState } from 'react';

// Animates a metric number counting up from 0 to its value. Callers mount
// this only once the real number is known (the `loading ? <Skeleton/> :
// <ThisNumber/>` pattern already used across the metric cards) — so the
// 0-to-target animation plays exactly once, right as the Skeleton is
// replaced by the real figure. Later value changes (polling, a filter) also
// animate, from the previous displayed value.
const useCountUp = (value, duration = 600) => {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const start = prevRef.current;
    if (start === target) return;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        prevRef.current = target;
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => frameRef.current && cancelAnimationFrame(frameRef.current);
    // eslint-disable-next-line
  }, [target, duration]);

  return display;
};

export default useCountUp;
